import React, { useEffect, useState } from 'react';
import { logger } from '../../utils/logger';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, EyeIcon, PhoneIcon, MailIcon, CalendarIcon, ClockIcon, DownloadIcon, FileSpreadsheetIcon, UserIcon, LockIcon, ArchiveIcon, ArchiveRestoreIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, FileTextIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Modal } from '../ui/Modal';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Doctor, DoctorSchedule, Gender } from '../../types';
import { formatTime, getStatusName, getDoctorPrefix } from '../../utils/helpers';
import { exportDoctorsToExcel } from '../../utils/excelExport';
import { exportDoctorsToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { validateDoctorForm } from '../../utils/validation';
import { useSortableData } from '../../hooks/useSortableData';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_NAMES: Record<string, string> = {
  monday: 'Lunes',
  tuesday: 'Martes',
  wednesday: 'Miércoles',
  thursday: 'Jueves',
  friday: 'Viernes',
  saturday: 'Sábado',
  sunday: 'Domingo'
};
export function Doctors({
  autoOpenNew = false
}: {
  autoOpenNew?: boolean;
} = {}) {
  const {
    doctors,
    addDoctor,
    updateDoctor,
    deleteDoctor,
    specialties,
    getSpecialty,
    getAppointmentsByDoctor,
    getPatient,
    getService
  } = useData();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    confirm
  } = useConfirm();
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('doctors');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | 'delete' | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-doctors-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  // Load deactivated/archived statuses for specialties (to filter them from dropdowns)
  const [specialtyStatuses, setSpecialtyStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-specialties-status');
      if (saved) setSpecialtyStatuses(JSON.parse(saved));
    } catch {}
    // Listen for storage changes from other tabs/modules
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'savidna-specialties-status' && e.newValue) {
        try {
          setSpecialtyStatuses(JSON.parse(e.newValue));
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const isSpecialtyActive = (id: string) => !specialtyStatuses[id];
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateDoctor(id, {
      status: 'archived'
    } as any).catch(e => logger.error('Error archiving doctor in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateDoctor(id, {
      status: 'deactivated'
    } as any).catch(e => logger.error('Error deactivating doctor in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateDoctor(id, {
      status: 'active'
    } as any).catch(e => logger.error('Error restoring doctor in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    gender: 'masculino' as Gender,
    specialtyId: '',
    phone: '',
    email: ''
  });
  const [scheduleData, setScheduleData] = useState<DoctorSchedule>({
    monday: {
      start: '08:00',
      end: '17:00'
    },
    tuesday: {
      start: '08:00',
      end: '17:00'
    },
    wednesday: {
      start: '08:00',
      end: '17:00'
    },
    thursday: {
      start: '08:00',
      end: '17:00'
    },
    friday: {
      start: '08:00',
      end: '14:00'
    },
    saturday: null,
    sunday: null
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const filteredDoctors = doctors.filter(d => {
    const matchesSearch = d.name.toLowerCase().includes(searchTerm.toLowerCase()) || d.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSpecialty = !filterSpecialty || d.specialtyId === filterSpecialty;
    const status = getItemStatus(d.id);
    return matchesSearch && matchesSpecialty && status === viewMode;
  });
  const {
    sortedItems: sortedDoctors,
    requestSort,
    isSorted
  } = useSortableData(filteredDoctors);
  const {
    paginatedItems: paginatedDoctors,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(sortedDoctors);
  // Counts for tabs
  const activeCount = doctors.filter(d => getItemStatus(d.id) === 'active').length;
  const archivedCount = doctors.filter(d => getItemStatus(d.id) === 'archived').length;
  const deactivatedCount = doctors.filter(d => getItemStatus(d.id) === 'deactivated').length;
  const specialtyOptions = [{
    value: '',
    label: 'Todas las especialidades'
  }];
  const specialtyOptionsForForm = specialties.filter(s => isSpecialtyActive(s.id)).map(s => ({
    value: s.id,
    label: s.name
  }));
  const openNewForm = () => {
    if (!canCreate('doctors')) return;
    setSelectedDoctor(null);
    setFormData({
      name: '',
      gender: 'masculino',
      specialtyId: '',
      phone: '',
      email: ''
    });
    setScheduleData({
      monday: {
        start: '08:00',
        end: '17:00'
      },
      tuesday: {
        start: '08:00',
        end: '17:00'
      },
      wednesday: {
        start: '08:00',
        end: '17:00'
      },
      thursday: {
        start: '08:00',
        end: '17:00'
      },
      friday: {
        start: '08:00',
        end: '14:00'
      },
      saturday: null,
      sunday: null
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  // Auto-open new form when navigated with :new param
  useEffect(() => {
    if (autoOpenNew) {
      const t = setTimeout(() => openNewForm(), 100);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNew]);
  const openEditForm = (doctor: Doctor) => {
    if (!canEdit('doctors')) return;
    setSelectedDoctor(doctor);
    setFormData({
      name: doctor.name,
      gender: doctor.gender || 'masculino',
      specialtyId: doctor.specialtyId,
      phone: doctor.phone,
      email: doctor.email
    });
    setScheduleData(doctor.schedule);
    setFormErrors({});
    setIsViewOpen(false);
    setIsFormOpen(true);
  };
  const openView = (doctor: Doctor) => {
    setSelectedDoctor(doctor);
    setActiveTab('info');
    setIsViewOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateDoctorForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    confirm({
      title: selectedDoctor ? 'Guardar Cambios' : 'Crear Doctor',
      message: selectedDoctor ? '¿Estás seguro de que deseas guardar los cambios de este doctor?' : '¿Estás seguro de que deseas registrar a este nuevo doctor?',
      confirmText: 'Guardar',
      onConfirm: () => {
        if (selectedDoctor) {
          updateDoctor(selectedDoctor.id, {
            ...formData,
            schedule: scheduleData
          });
          showToast({
            type: 'success',
            title: 'Doctor actualizado',
            message: `${formData.name} fue actualizado correctamente.`
          });
        } else {
          addDoctor({
            ...formData,
            schedule: scheduleData
          });
          showToast({
            type: 'success',
            title: 'Doctor creado',
            message: `${formData.name} fue registrado correctamente.`
          });
        }
        setIsFormOpen(false);
      }
    });
  };
  const openActionConfirm = (doctor: Doctor, type: 'archive' | 'deactivate' | 'delete') => {
    setSelectedDoctor(doctor);
    let title = '';
    let message = '';
    let confirmText = '';
    let variant: 'danger' | 'warning' | 'info' | 'primary' = 'primary';
    if (type === 'archive') {
      title = 'Archivar Doctor';
      message = `¿Estás seguro de que deseas archivar a "${doctor.name}"? Podrás restaurarlo más tarde.`;
      confirmText = 'Archivar';
      variant = 'warning';
    } else if (type === 'deactivate') {
      title = 'Desactivar Doctor';
      message = `¿Estás seguro de que deseas desactivar a "${doctor.name}"? No aparecerá en las listas activas.`;
      confirmText = 'Desactivar';
      variant = 'warning';
    } else if (type === 'delete') {
      title = 'Eliminar Doctor';
      message = `¿Estás seguro de que deseas eliminar permanentemente a "${doctor.name}"? Esta acción no se puede deshacer.`;
      confirmText = 'Eliminar';
      variant = 'danger';
    }
    confirm({
      title,
      message,
      confirmText,
      variant,
      onConfirm: () => {
        if (type === 'archive') {
          archiveItem(doctor.id);
          showToast({
            type: 'info',
            title: 'Doctor archivado',
            message: `${doctor.name} fue archivado.`
          });
        } else if (type === 'deactivate') {
          deactivateItem(doctor.id);
          showToast({
            type: 'warning',
            title: 'Doctor desactivado',
            message: `${doctor.name} fue desactivado.`
          });
        } else if (type === 'delete') {
          showToast({
            type: 'error',
            title: 'Doctor eliminado',
            message: `${doctor.name} fue eliminado permanentemente.`
          });
          deleteDoctor(doctor.id);
          setIsViewOpen(false);
        }
      }
    });
  };
  const toggleDaySchedule = (day: keyof DoctorSchedule) => {
    setScheduleData(prev => ({
      ...prev,
      [day]: prev[day] ? null : {
        start: '08:00',
        end: '17:00'
      }
    }));
  };
  const updateDaySchedule = (day: keyof DoctorSchedule, field: 'start' | 'end', value: string) => {
    setScheduleData(prev => ({
      ...prev,
      [day]: prev[day] ? {
        ...prev[day]!,
        [field]: value
      } : null
    }));
  };
  const tabs = [{
    id: 'info',
    label: 'Información',
    icon: <ClockIcon className="w-4 h-4" data-id="element-3003" data-mp="33xn73zjrvi79" />
  }, {
    id: 'schedule',
    label: 'Horario',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-3004" data-mp="3iunz845vlf69" />
  }, {
    id: 'appointments',
    label: 'Citas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-3005" data-mp="3dwco7q3ugymg" />
  }];
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = doctors;
    if (dateFrom && dateTo) {
      dataToExport = doctors.filter(d => {
        const dateStr = new Date(d.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportDoctorsToExcel(dataToExport, (specialtyId: string) => {
      const specialty = getSpecialty(specialtyId);
      return specialty?.name || '-';
    });
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = doctors;
    if (dateFrom && dateTo) {
      dataToExport = doctors.filter(d => {
        const dateStr = new Date(d.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportDoctorsToPdf(dataToExport, (specialtyId: string) => {
      const specialty = getSpecialty(specialtyId);
      return specialty?.name || '-';
    });
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-3006" data-mp="2wrht4t5a1cq0">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 shadow-xl text-white" data-id="element-3007" data-mp="2wpx0j92iw3us">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 19px, rgba(255,255,255,1) 19px, rgba(255,255,255,1) 20px)'
      }} data-id="element-3008" data-mp="2nqs180j506hk" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-3009" data-mp="2902o2sm48hb2" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-3010" data-mp="200fjxz204xeb" />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full border-[40px] border-white/10 pointer-events-none" data-id="element-3011" data-mp="1v249pn7sokvk" />
        <div className="absolute -bottom-10 right-32 w-40 h-40 rounded-full opacity-10 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)'
      }} data-id="element-3012" data-mp="1ld74gm1nu5wj" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-3013" data-mp="1gevu8a7gdtds" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-3014" data-mp="244q6apjy7dwz">
          <div data-id="element-3015" data-mp="293b82teum5nj">
            <div className="flex items-center gap-3 mb-2" data-id="element-3016" data-mp="293te2hrbby8n">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-3017" data-mp="1ufzyuy652xql">
                <UserIcon className="w-6 h-6 text-white" data-id="element-3018" data-mp="1ze1ibk7h8z2l" />
              </div>
              <h1 className="text-2xl font-bold text-white" data-id="element-3019" data-mp="2n445pn0g5guv">Doctores</h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-3020" data-mp="2n2az27pu2wmv">
              Gestión de doctores y especialidades
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-3021" data-mp="gekb22uhid87">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-3022" data-mp="q4n2vzpvdl4y">
            {canExportPdf && <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-3024" data-mp="l8y0lomgtq4y" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-3023" data-mp="l623u3mct7i3">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-3026" data-mp="2igi0cvckaxb" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-3025" data-mp="2fuecobkufkd">
                Exportar Excel
              </Button>}
            {canCreate('doctors') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3028" data-mp="3cdcfcze4mjh8" />} onClick={openNewForm} className="bg-white text-teal-700 hover:bg-teal-50 shadow-lg shadow-teal-900/20" data-id="element-3027" data-mp="3cc9rz1g0iz62">
                Nuevo Doctor
              </Button>}
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-3029" data-mp="3v3kd11z24hq1">
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto" data-id="element-3030" data-mp="3v5dmep121g1q">
          <div className="w-full sm:w-64 relative" data-id="element-3031" data-mp="3xspmacnzj6x">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-3032" data-mp="8wvw7z8alxh0" />
            <Input style={{
            paddingLeft: '2.5rem'
          }} placeholder="Buscar por nombre o correo..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-3033" data-mp="25pnxeevsv3jv" />
          </div>
          <div className="w-full sm:w-64" data-id="element-3034" data-mp="j016qocohyrl">
            <Select options={specialtyOptions} value={filterSpecialty} onChange={setFilterSpecialty} placeholder="Filtrar por especialidad" data-id="element-3035" data-mp="j0ss76ckag45" />
          </div>
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-3036" data-mp="16plrncse763i">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-3037" data-mp="3och0fndzq95w">
              <tab.icon className="w-4 h-4" data-id="element-3038" data-mp="5zsurg9a09n8" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-3039" data-mp="625bxr77mfz8">
                {tab.count}
              </span>
            </button>)}
        </div>
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-3040" data-mp="2lqyn9rrh4hn1">
          <LockIcon className="w-4 h-4" data-id="element-3041" data-mp="16892a7dbqvx0" />
          <span data-id="element-3042" data-mp="169agultju0jh">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            doctores.
          </span>
        </div>}

      {/* Doctors List */}
      <Card padding="none" className="overflow-hidden border-0 shadow-lg" data-id="element-3043" data-mp="1fzdhn86wwisd">
        <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-3044" data-mp="1kxoorb5tof6w" />

        {/* ── MOBILE CARDS ── */}
        <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3045" data-mp="1oygye5ilwm3p">
          {paginatedDoctors.length === 0 ? <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-3046" data-mp="1tzeemck2362k">
              <UserIcon className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" data-id="element-3047" data-mp="1yw4w94bie9q3" />
              <p className="text-sm" data-id="element-3048" data-mp="3vq9i7qki89ju">
                {searchTerm ? 'No se encontraron resultados' : 'No hay doctores registrados'}
              </p>
            </div> : paginatedDoctors.map(doctor => {
          const specialty = getSpecialty(doctor.specialtyId);
          return <div key={doctor.id} className="px-4 py-3 flex items-center gap-3" data-id="element-3049" data-mp="3oomfaq76g0c7">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center text-white font-bold shadow-sm flex-shrink-0" data-id="element-3050" data-mp="1hajkfrt8ycu">
                    {doctor.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3051" data-mp="bdx1owsr5bb6">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-3052" data-mp="p5ti43l25wrq">
                      {getDoctorPrefix(doctor.gender, specialty?.name)}{' '}
                      {doctor.name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap" data-id="element-3053" data-mp="z37zq6ipwrlm">
                      <span className="text-xs bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 px-2 py-0.5 rounded-full" data-id="element-3054" data-mp="u7sj56tbshli">
                        {specialty?.name || '-'}
                      </span>
                      {doctor.phone && <span className="text-xs text-gray-400" data-id="element-3055" data-mp="13ud4hpa56c4u">
                          {doctor.phone}
                        </span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-3056" data-mp="u3s35u332ti8">
                    {viewMode === 'active' && <>
                        <button onClick={() => openEditForm(doctor)} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Editar" data-id="element-3057" data-mp="ywlamppxsfy0">
                          <EditIcon className="w-4 h-4" data-id="element-3058" data-mp="g499u7exlmpz" />
                        </button>
                        <button onClick={() => openScheduleEditor(doctor)} className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Horario" data-id="element-3059" data-mp="g63lsw8864hx">
                          <CalendarIcon className="w-4 h-4" data-id="element-3060" data-mp="14qsaval00xv5" />
                        </button>
                      </>}
                    {viewMode !== 'active' && <button onClick={() => restoreItem(doctor.id)} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Restaurar" data-id="element-3061" data-mp="vpu3qjf9ka8c">
                        <RotateCcwIcon className="w-4 h-4" data-id="element-3062" data-mp="ltr53njmetq5" />
                      </button>}
                  </div>
                </div>;
        })}
        </div>

        {/* ── DESKTOP TABLE ── */}
        <div className="hidden sm:block overflow-x-auto" data-id="element-3063" data-mp="2npq43mbgpfgs">
          <table className="w-full" data-id="element-3064" data-mp="2nn3xboweb3zm">
            <thead data-id="element-3065" data-mp="2sn9nt1qxikg4">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-3066" data-mp="2smgmigaqon5d">
                <SortableHeader label="Doctor" sortKey="name" currentDirection={isSorted('name')} onSort={requestSort} data-id="element-3067" data-mp="2xfjhmsv604no" />

                <SortableHeader label="Especialidad" sortKey="specialtyId" currentDirection={isSorted('specialtyId')} onSort={requestSort} data-id="element-3068" data-mp="24s6x0o8ke6x0" />

                <SortableHeader label="Contacto" sortKey="phone" currentDirection={isSorted('phone')} onSort={requestSort} className="hidden sm:table-cell" data-id="element-3069" data-mp="2jn3d1nmm7ykn" />

                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3070" data-mp="3v4mkjs78anp0">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3071" data-mp="240yofmetlc0i">
              {filteredDoctors.length === 0 ? <tr data-id="element-3072" data-mp="1zz3oomcjf6ou">
                  <td colSpan={4} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-3073" data-mp="200oig9r12lln">
                    <div className="flex flex-col items-center justify-center" data-id="element-3074" data-mp="17ay2dv5u59mw">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-3075" data-mp="178v4koyfyc7m">
                        <UserIcon className="w-8 h-8 text-gray-400" data-id="element-3076" data-mp="1gaul7g5jnea1" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-3077" data-mp="sl9zipi0a2x2">
                        No se encontraron doctores
                      </h3>
                      <p className="text-sm" data-id="element-3078" data-mp="oidmrff5d111">
                        {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza registrando un nuevo doctor' : 'No hay doctores en esta vista'}
                      </p>
                    </div>
                  </td>
                </tr> : paginatedDoctors.map(doctor => {
              const specialty = getSpecialty(doctor.specialtyId);
              return <tr key={doctor.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-3079" data-mp="123huji8rn5cu">
                      <td className="py-4 px-6" data-id="element-3080" data-mp="s9racpjo9h2y">
                        <div className="flex items-center gap-3" data-id="element-3081" data-mp="37yj5gljpw0s1">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center text-white font-bold shadow-sm" data-id="element-3082" data-mp="3cxvrx56of5u3">
                            {doctor.name.charAt(0).toUpperCase()}
                          </div>
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-3083" data-mp="2p6yki4nxvygf">
                            {getDoctorPrefix(doctor.gender, specialty?.name)}{' '}
                            {doctor.name}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-3084" data-mp="3cx2nc9tazu6d">
                        <Badge variant="primary" className="bg-teal-50 text-teal-700 border border-teal-100" data-id="element-3085" data-mp="37y98oe2a5x4m">
                          {specialty?.name || '-'}
                        </Badge>
                      </td>
                      <td className="py-4 px-6 hidden sm:table-cell" data-id="element-3086" data-mp="3vo3n3028ztqz">
                        <div className="space-y-1" data-id="element-3087" data-mp="3vq6k441tin57">
                          <p className="text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1.5" data-id="element-3088" data-mp="3qwkfmz5t5pkh">
                            <PhoneIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-3089" data-mp="1dgalmgr24dt7" />{' '}
                            {doctor.phone}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5" data-id="element-3090" data-mp="1mfxeuf9mudb6">
                            <MailIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-3091" data-mp="1rfa1p00i7kdr" />{' '}
                            {doctor.email}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-3092" data-mp="21c4wb7tnp0mr">
                        <div className="flex items-center justify-end gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-id="element-3093" data-mp="21attba81bwwp">
                          <button onClick={() => openView(doctor)} className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Ver detalles" data-id="element-3094" data-mp="2a5hzr3pcjkn3">
                            <EyeIcon className="w-4 h-4" data-id="element-3095" data-mp="3kd0l2c91mpfn" />
                          </button>

                          {viewMode === 'active' ? <>
                              {canEdit('doctors') && <button onClick={() => openEditForm(doctor)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-3096" data-mp="3gcyt4ut41adk">
                                  <EditIcon className="w-4 h-4" data-id="element-3097" data-mp="3ubyh008stl8o" />
                                </button>}
                              {canDelete('doctors') && <>
                                  <button onClick={() => openActionConfirm(doctor, 'archive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-3098" data-mp="qty72fjon5sf">
                                    <ArchiveIcon className="w-4 h-4" data-id="element-3099" data-mp="19tb4opuy77vp" />
                                  </button>
                                  <button onClick={() => openActionConfirm(doctor, 'deactivate')} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-3100" data-mp="m3gukg20jxf5">
                                    <PowerOffIcon className="w-4 h-4" data-id="element-3101" data-mp="22h76ua3adrzv" />
                                  </button>
                                </>}
                            </> : <button onClick={() => restoreItem(doctor.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-3102" data-mp="2ehx86c23996i">
                              <RotateCcwIcon className="w-4 h-4" data-id="element-3103" data-mp="1qrt4y5stbbg1" />
                            </button>}
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-6 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4" data-id="element-3104" data-mp="183xtiskoa9q3">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-3105" data-mp="1d1pknkdhtrt8" />
        </div>
      </Card>

      {/* Doctor Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedDoctor ? 'Editar Doctor' : 'Nuevo Doctor'} size="lg" icon={<UserIcon className="w-5 h-5" data-id="element-3107" data-mp="mlf88buzu32v" />} accentColor="sky" data-id="element-3106" data-mp="vmdbgrxly6ke">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3108" data-mp="hs462o14maha">
          {/* Personal Info Section */}
          <div data-id="element-3109" data-mp="cv400d4a17q6">
            <div className="flex items-center gap-2 mb-4" data-id="element-3110" data-mp="cukg4qv2g99i">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-3111" data-mp="7vhlm0cpplfs"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3112" data-mp="2vvabwgg2f9m">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3113" data-mp="3v1s4c0ri5qus">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3114" data-mp="sjqyb1xw3gfl">
                <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} error={formErrors.name} placeholder="Nombre del doctor" data-id="element-3115" data-mp="sm4uje4b2yoq" />

                <Select label="Género" options={[{
                value: 'masculino',
                label: 'Masculino'
              }, {
                value: 'femenino',
                label: 'Femenino'
              }]} value={formData.gender} onChange={value => setFormData({
                ...formData,
                gender: value as Gender
              })} data-id="element-3116" data-mp="9r6ba14vg5jp" />
              </div>
              <Select label="Especialidad" options={specialtyOptionsForForm} value={formData.specialtyId} onChange={value => setFormData({
              ...formData,
              specialtyId: value
            })} error={formErrors.specialtyId} data-id="element-3117" data-mp="3ohhtw0nznca2" />
            </div>
          </div>

          {/* Contact Section */}
          <div data-id="element-3118" data-mp="6bli1ez2cari">
            <div className="flex items-center gap-2 mb-4" data-id="element-3119" data-mp="6b3f5zhwayam">
              <span className="w-1.5 h-5 rounded-full bg-cyan-500" data-id="element-3120" data-mp="69ij25fxn7an"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3121" data-mp="2d2uidevnx1ri">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3122" data-mp="c0d1d6qar0b7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3123" data-mp="gzg94yxnvkmv">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} error={formErrors.phone} placeholder="809-000-0000" data-id="element-3124" data-mp="lxri77g4tr41" />

                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} error={formErrors.email} placeholder="doctor@email.com" data-id="element-3125" data-mp="14o8w45wb2i5c" />
              </div>
            </div>
          </div>

          {/* Schedule Section */}
          <div data-id="element-3126" data-mp="2y5oel716s5k9">
            <div className="flex items-center gap-2 mb-4" data-id="element-3127" data-mp="2y9c8w8eggw01">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-3128" data-mp="2u6nyvxe09j1h"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3129" data-mp="2p6t7lsqbirfw">
                Horario de Atención
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3130" data-mp="3lyn0p0dtvg21">
              <div className="space-y-1" data-id="element-3131" data-mp="3d3yvtbc600dr">
                {DAYS.map(day => {
                const isActive = scheduleData[day] !== null;
                return <div key={day} className={`p-3 rounded-lg transition-colors ${isActive ? 'bg-white dark:bg-gray-800 border border-teal-100 dark:border-teal-800/30 shadow-sm' : 'border border-transparent'}`} data-id="element-3132" data-mp="383kwqisjk0rv">
                      {/* Day row */}
                      <label className="flex items-center gap-2 cursor-pointer select-none" data-id="element-3133" data-mp="3dwyhrv5iix1l">
                        <div className="relative flex-shrink-0" data-id="element-3134" data-mp="2z8bzploi3r6o">
                          <input type="checkbox" checked={isActive} onChange={() => toggleDaySchedule(day)} className="sr-only peer" data-id="element-3135" data-mp="2za59h9ueuriw" />

                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${isActive ? 'bg-teal-500 border-teal-500 dark:bg-teal-600 dark:border-teal-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500'}`} data-id="element-3136" data-mp="pdj9u63co60">
                            {isActive && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-3137" data-mp="2bj9btw39x23q">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-3138" data-mp="2dw3fw4v2kogx" />
                              </svg>}
                          </div>
                        </div>
                        <span className={`text-sm font-semibold ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-3139" data-mp="1uyadd3ocygk2">
                          {DAY_NAMES[day]}
                        </span>
                      </label>

                      {/* Times row - always below day name */}
                      {isActive && <div className="flex items-center gap-2 mt-2 pl-7" data-id="element-3140" data-mp="3tzj45sse5b9a">
                          <input type="time" value={scheduleData[day]!.start} onChange={e => updateDaySchedule(day, 'start', e.target.value)} className="flex-1 min-w-0 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-gray-100 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400" data-id="element-3141" data-mp="3pyh5pe6at38d" />

                          <span className="text-gray-400 text-xs font-medium flex-shrink-0" data-id="element-3142" data-mp="3l1gygzxppufo">
                            a
                          </span>
                          <input type="time" value={scheduleData[day]!.end} onChange={e => updateDaySchedule(day, 'end', e.target.value)} className="flex-1 min-w-0 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-gray-100 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400" data-id="element-3143" data-mp="2si1mgn02d5un" />
                        </div>}
                    </div>;
              })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700 modal-actions" data-id="element-3144" data-mp="1cs0zgs6a1c3o">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-3145" data-mp="1hq2jbfbj1fg7">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-3146" data-mp="6c8573w9si34">
              {selectedDoctor ? 'Guardar Cambios' : 'Crear Doctor'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Doctor View Modal */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Detalles del Doctor" size="lg" data-id="element-3147" data-mp="2319j425yslc3">
        {selectedDoctor && <div className="space-y-6" data-id="element-3148" data-mp="1u9fzg6v52uia">
            {/* Hero Header */}
            <div className="relative rounded-xl overflow-hidden bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm" data-id="element-3149" data-mp="1u97mkez0np73">
              <div className="px-6 py-5" data-id="element-3150" data-mp="1kaqs380vcsga">
                <div className="flex items-center gap-4" data-id="element-3151" data-mp="1k9p5c6ahqhiq">
                  <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-teal-50 to-blue-50 dark:from-teal-900/50 dark:to-blue-900/50 flex items-center justify-center text-3xl font-bold text-teal-600 dark:text-teal-400 shadow-sm border border-gray-100 dark:border-gray-700 flex-shrink-0" data-id="element-3152" data-mp="1fbnlhj58qe67">
                    {selectedDoctor.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3153" data-mp="rh3pmgjh5zle">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white truncate" data-id="element-3154" data-mp="rgc45yjldi8u">
                      {getDoctorPrefix(selectedDoctor.gender, getSpecialty(selectedDoctor.specialtyId)?.name)}{' '}
                      {selectedDoctor.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-3155" data-mp="8i9dlfbebd8x">
                      {getSpecialty(selectedDoctor.specialtyId)?.name}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0" data-id="element-3156" data-mp="hi647mhcim6m">
                    {!readOnly && viewMode === 'active' && <>
                        {canEdit('doctors') && <Button variant="outline" size="sm" onClick={() => openEditForm(selectedDoctor)} data-id="element-3157" data-mp="1symcmvuhg2jz">
                            <EditIcon className="w-4 h-4 mr-2" data-id="element-3158" data-mp="1wgjtobsqd1x5" /> Editar
                          </Button>}
                        {canDelete('doctors') && <Button variant="danger" size="sm" onClick={() => openActionConfirm(selectedDoctor, 'archive')} data-id="element-3159" data-mp="25goz4ged5q8o">
                            <ArchiveIcon className="w-4 h-4" data-id="element-3160" data-mp="2agbeavdr5gk4" />
                          </Button>}
                      </>}
                  </div>
                </div>
              </div>
            </div>

            <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-3161" data-mp="2k7ft9v3fh1ey">
              <TabPanel id="info" activeTab={activeTab} data-id="element-3162" data-mp="2y5vw3ay667rt">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3163" data-mp="2t6awcyb6dve1">
                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700" data-id="element-3164" data-mp="2t46knu71bs37">
                    <div className="flex items-center gap-3 mb-3" data-id="element-3165" data-mp="3hrfpb9rxftx3">
                      <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400" data-id="element-3166" data-mp="3hotf12dnj0b6">
                        <PhoneIcon className="w-4 h-4" data-id="element-3167" data-mp="37roqivwc2sr7" />
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-3168" data-mp="2u11td8681ax8">
                        Teléfono
                      </h4>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-3169" data-mp="2k35m4rgen04e">
                      {selectedDoctor.phone}
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700" data-id="element-3170" data-mp="2p2i7f7rmnz4v">
                    <div className="flex items-center gap-3 mb-3" data-id="element-3171" data-mp="2b4toi5h4f22g">
                      <div className="w-8 h-8 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center text-cyan-600 dark:text-cyan-400" data-id="element-3172" data-mp="2g10qxoab5vfn">
                        <MailIcon className="w-4 h-4" data-id="element-3173" data-mp="gu3l18wuwbcp" />
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-3174" data-mp="bv95cfu6r2wt">
                        Correo Electrónico
                      </h4>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-3175" data-mp="7s438ee0p38p">
                      {selectedDoctor.email}
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700" data-id="element-3176" data-mp="zmegihp6l70s">
                    <div className="flex items-center gap-3 mb-3" data-id="element-3177" data-mp="vlm8tsvyja6m">
                      <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400" data-id="element-3178" data-mp="qjn5uk44qfa7">
                        <UserIcon className="w-4 h-4" data-id="element-3179" data-mp="3baxbqcrxv1w0" />
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-3180" data-mp="3bd0abl75q3cc">
                        Género
                      </h4>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 pl-11 capitalize" data-id="element-3181" data-mp="1vs7sawtfto83">
                      {selectedDoctor.gender || 'No especificado'}
                    </p>
                  </div>
                </div>
              </TabPanel>

              <TabPanel id="schedule" activeTab={activeTab} data-id="element-3182" data-mp="29jcxsfjhlaqc">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3183" data-mp="29iuw33e224b1">
                  {DAYS.map(day => <div key={day} className={`flex items-center justify-between p-3 rounded-lg border ${selectedDoctor.schedule[day] ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'bg-gray-50 dark:bg-gray-800/50 border-transparent'}`} data-id="element-3184" data-mp="2ek0of03szpk1">
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-3185" data-mp="t4l31vyvbrdv">
                        {DAY_NAMES[day]}
                      </span>
                      {selectedDoctor.schedule[day] ? <span className="text-sm font-medium text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-2 py-1 rounded" data-id="element-3186" data-mp="5a17kuh0letl">
                          {formatTime(selectedDoctor.schedule[day]!.start)} -{' '}
                          {formatTime(selectedDoctor.schedule[day]!.end)}
                        </span> : <span className="text-sm text-gray-400 italic" data-id="element-3187" data-mp="3ngz66hwbir9p">
                          No disponible
                        </span>}
                    </div>)}
                </div>
              </TabPanel>

              <TabPanel id="appointments" activeTab={activeTab} data-id="element-3188" data-mp="3rfoj69dg26st">
                <div className="space-y-4" data-id="element-3189" data-mp="1bpbog9c89dz0">
                  {getAppointmentsByDoctor(selectedDoctor.id).length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-3190" data-mp="16pgxy6wd6qeh">
                      <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-3191" data-mp="1j94g5keaq32m" />
                      <p className="text-gray-500 dark:text-gray-400" data-id="element-3192" data-mp="1j7058ihzc3su">
                        No hay citas registradas
                      </p>
                    </div> : getAppointmentsByDoctor(selectedDoctor.id).map(apt => {
                const patient = getPatient(apt.patientId);
                const service = getService(apt.serviceId);
                return <div key={apt.id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl hover:shadow-sm transition-shadow" data-id="element-3193" data-mp="1o8pgnz946jhg">
                          <div className="flex items-center gap-4" data-id="element-3194" data-mp="2lnbfm8btby4p">
                            <div className="w-10 h-10 rounded-full bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center text-teal-600 dark:text-teal-400 font-bold" data-id="element-3195" data-mp="2lpo4z6jwhars">
                              {patient?.name.charAt(0)}
                            </div>
                            <div data-id="element-3196" data-mp="19c7o0d35lhr">
                              <p className="font-medium text-gray-900 dark:text-white" data-id="element-3197" data-mp="1a4237uy5d6g">
                                {patient?.name}
                              </p>
                              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-3198" data-mp="34q1dtl8rat6w">
                                <span data-id="element-3199" data-mp="39o2zmdxkh6m2">{apt.date}</span>
                                <span data-id="element-3200" data-mp="3eme5kdkqqwz0">•</span>
                                <span data-id="element-3201" data-mp="3elmd3bmfxfcy">{formatTime(apt.time)}</span>
                                <span data-id="element-3202" data-mp="3ipkanhw4ai3w">•</span>
                                <span data-id="element-3203" data-mp="3im68a9nge8wg">{service?.name}</span>
                              </div>
                            </div>
                          </div>
                          <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-3204" data-mp="xwyztm34mhpe">
                            {getStatusName(apt.status)}
                          </Badge>
                        </div>;
              })}
                </div>
              </TabPanel>
            </Tabs>
          </div>}
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Doctores" isExporting={isExporting} data-id="element-3205" data-mp="24nv0d8ymm8kk" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Doctores a PDF" isExporting={isPdfExporting} data-id="element-3206" data-mp="1qp523q02m2um" />
    </div>;
}